import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { AnswerValue, Question } from '../api/types';
import { ALL_COUNTRIES, COMPACT_COUNTRIES, countryOptions, guessCountry } from '../data/countries';
import { labelForFieldCode } from '../i18n/labels';
import { useZeitwahl } from '../provider';
import { Listbox } from './listbox';

export interface Prefill {
  name?: string;
  email?: string;
  phone?: string;
  phoneCountry?: string;
  /** Visitor's preferred language for emails; defaults to the widget locale. */
  locale?: string;
}

export interface BookingFormValues {
  name: string;
  email: string;
  phone?: string;
  phoneCountry?: string;
  answers: Record<string, AnswerValue>;
}

export interface BookingFormProps {
  questions: Question[];
  prefill?: Prefill;
  /** Used to guess the phone country when neither prefill nor locale pins one. */
  timeZone?: string;
  locale?: string;
  /** Server-side `validation_failed.fields`: `visitor.email` / `answers.<id>` → field code. */
  fieldErrors?: Record<string, string>;
  submitting?: boolean;
  disabled?: boolean;
  /** Renders a required consent checkbox with this content (e.g. a privacy-policy link). */
  consentText?: ReactNode;
  submitLabel?: string;
  /** Move focus to the form heading when it mounts (used when the flow advances to this step). */
  focusOnMount?: boolean;
  onSubmit: (values: BookingFormValues) => void;
  children?: ReactNode;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[0-9 ()./-]{5,25}$/;
const MORE = '__more';

const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/** `visitor.email` → `email`; `answers.q1` stays; unknown paths pass through. */
const normalisePath = (path: string): string => path.replace(/^visitor\./, '');

type Errors = Record<string, string>;

/**
 * Visitor details + custom questions. Validates client-side (required, email, phone + country, URL,
 * choice counts), merges server field codes, and wires every message via `aria-describedby`.
 */
export function BookingForm(props: BookingFormProps): ReactNode {
  const { questions, prefill, fieldErrors, submitting, disabled, consentText, onSubmit, children } =
    props;
  const { t, labels, locale: contextLocale } = useZeitwahl();
  const locale = props.locale ?? contextLocale;
  const baseId = useId();
  const id = (name: string) => `${baseId}-${name}`;
  const formRef = useRef<HTMLFormElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [name, setName] = useState(prefill?.name ?? '');
  const [email, setEmail] = useState(prefill?.email ?? '');
  const [phone, setPhone] = useState(prefill?.phone ?? '');
  const [phoneCountry, setPhoneCountry] = useState(
    () => prefill?.phoneCountry?.toUpperCase() ?? guessCountry(locale, props.timeZone ?? '') ?? ''
  );
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [consent, setConsent] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(
    () => !!phoneCountry && !(COMPACT_COUNTRIES as readonly string[]).includes(phoneCountry)
  );
  const [clientErrors, setClientErrors] = useState<Errors>({});
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => setDismissed(new Set()), [fieldErrors]);
  useEffect(() => {
    if (props.focusOnMount) headingRef.current?.focus();
  }, [props.focusOnMount]);

  const countries = useMemo(
    () =>
      countryOptions(
        showAllCountries ? ALL_COUNTRIES : COMPACT_COUNTRIES,
        locale,
        showAllCountries
      ),
    [showAllCountries, locale]
  );

  const errors = useMemo<Errors>(() => {
    const merged: Errors = {};
    for (const [path, code] of Object.entries(fieldErrors ?? {})) {
      const key = normalisePath(path);
      if (!dismissed.has(key)) merged[key] = code;
    }
    return { ...merged, ...clientErrors };
  }, [fieldErrors, dismissed, clientErrors]);

  const clearError = (key: string) => {
    setDismissed((s) => (s.has(key) ? s : new Set(s).add(key)));
    setClientErrors((e) => {
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const errorText = (key: string): string | undefined => {
    const code = errors[key];
    if (!code) return undefined;
    const question = questions.find((q) => `answers.${q.id}` === key);
    const vars =
      question?.type === 'multiselect'
        ? { min: question.min ?? 0, max: question.max ?? 0 }
        : undefined;
    return labelForFieldCode(labels, code, vars);
  };

  const validate = (): Errors => {
    const e: Errors = {};
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedName) e.name = 'required';
    else if (trimmedName.length > 200) e.name = 'too_long';
    if (!trimmedEmail) e.email = 'required';
    else if (!EMAIL_RE.test(trimmedEmail)) e.email = 'invalid_email_format';
    if (trimmedPhone) {
      if (!PHONE_RE.test(trimmedPhone)) e.phone = 'invalid_phone';
      if (!phoneCountry) e.phoneCountry = 'phone_country_required';
    }
    for (const q of questions) {
      const key = `answers.${q.id}`;
      const value = answers[q.id];
      if (q.type === 'checkbox') {
        if (q.required && value !== true) e[key] = 'required';
      } else if (q.type === 'multiselect') {
        const picked = Array.isArray(value) ? value : [];
        if (q.required && picked.length === 0) e[key] = 'required';
        else if (q.min && picked.length < q.min) e[key] = 'min_choices';
        else if (q.max && picked.length > q.max) e[key] = 'max_choices';
      } else {
        const text = typeof value === 'string' ? value.trim() : '';
        if (q.required && !text) e[key] = 'required';
        else if (text) {
          if (q.type === 'email' && !EMAIL_RE.test(text)) e[key] = 'invalid_email_format';
          else if (q.type === 'url' && !isHttpUrl(text)) e[key] = 'invalid_url';
          else if (q.type === 'phone' && !PHONE_RE.test(text)) e[key] = 'invalid_phone';
          else if ('maxLength' in q && q.maxLength && text.length > q.maxLength)
            e[key] = 'too_long';
        }
      }
    }
    if (consentText && !consent) e.consent = 'required';
    return e;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled || submitting) return;
    const found = validate();
    setClientErrors(found);
    if (Object.keys(found).length > 0) {
      // Defer so the invalid state is in the DOM before focusing the first failing control.
      setTimeout(
        () => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
        0
      );
      return;
    }
    const cleaned: Record<string, AnswerValue> = {};
    for (const q of questions) {
      const value = answers[q.id];
      if (q.type === 'checkbox') cleaned[q.id] = value === true;
      else if (Array.isArray(value)) {
        if (value.length) cleaned[q.id] = value;
      } else if (typeof value === 'string' && value.trim()) cleaned[q.id] = value.trim();
    }
    const trimmedPhone = phone.trim();
    onSubmit({
      name: name.trim(),
      email: email.trim(),
      phone: trimmedPhone || undefined,
      phoneCountry: trimmedPhone ? phoneCountry || undefined : undefined,
      answers: cleaned,
    });
  };

  const describedBy = (key: string) => (errors[key] ? id(`${key}-error`) : undefined);
  const invalid = (key: string) => (errors[key] ? true : undefined);

  const renderError = (key: string) => {
    const text = errorText(key);
    return text ? (
      <p id={id(`${key}-error`)} className="zw-error">
        {text}
      </p>
    ) : null;
  };

  const requiredMark = (required: boolean) =>
    required ? (
      <span className="zw-required" aria-hidden="true">
        {' *'}
      </span>
    ) : (
      <span className="zw-muted zw-optional"> ({t('optional')})</span>
    );

  const setAnswer = (qid: string, value: AnswerValue) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    clearError(`answers.${qid}`);
  };

  const renderQuestion = (q: Question): ReactNode => {
    const key = `answers.${q.id}`;
    const fieldId = id(q.id);
    const hasError = !!errors[key];
    const wrapClass = `zw-field${hasError ? ' zw-field--invalid' : ''}`;
    switch (q.type) {
      case 'textarea':
        return (
          <div key={q.id} className={wrapClass}>
            <label className="zw-label" htmlFor={fieldId}>
              {q.label}
              {requiredMark(q.required)}
            </label>
            <textarea
              id={fieldId}
              className="zw-input zw-textarea"
              value={typeof answers[q.id] === 'string' ? (answers[q.id] as string) : ''}
              placeholder={q.placeholder}
              maxLength={q.maxLength}
              required={q.required}
              aria-required={q.required || undefined}
              aria-invalid={invalid(key)}
              aria-describedby={describedBy(key)}
              onChange={(e) => setAnswer(q.id, e.target.value)}
            />
            {renderError(key)}
          </div>
        );
      case 'select':
        return (
          <div key={q.id} className={wrapClass}>
            <label className="zw-label" htmlFor={fieldId}>
              {q.label}
              {requiredMark(q.required)}
            </label>
            <Listbox
              id={fieldId}
              value={typeof answers[q.id] === 'string' ? (answers[q.id] as string) : ''}
              options={q.options.map((o) => ({ value: o.value, label: o.label }))}
              placeholder={t('formSelectPlaceholder')}
              aria-required={q.required}
              aria-invalid={invalid(key)}
              aria-describedby={describedBy(key)}
              onChange={(next) => setAnswer(q.id, next)}
            />
            {renderError(key)}
          </div>
        );
      case 'radio':
      case 'multiselect': {
        const multi = q.type === 'multiselect';
        const current = answers[q.id];
        const picked = multi ? (Array.isArray(current) ? current : []) : [];
        return (
          <fieldset
            key={q.id}
            className={`${wrapClass} zw-fieldset`}
            aria-required={q.required || undefined}
            aria-invalid={invalid(key)}
            aria-describedby={describedBy(key)}
          >
            <legend className="zw-label">
              {q.label}
              {requiredMark(q.required)}
            </legend>
            <div className="zw-choices">
              {q.options.map((o) => {
                const optionId = `${fieldId}-${o.value}`;
                const checked = multi ? picked.includes(o.value) : current === o.value;
                return (
                  <label key={o.value} className="zw-choice" htmlFor={optionId}>
                    <input
                      id={optionId}
                      type={multi ? 'checkbox' : 'radio'}
                      name={fieldId}
                      value={o.value}
                      checked={checked}
                      onChange={() =>
                        setAnswer(
                          q.id,
                          multi
                            ? checked
                              ? picked.filter((v) => v !== o.value)
                              : [...picked, o.value]
                            : o.value
                        )
                      }
                    />
                    <span>{o.label}</span>
                  </label>
                );
              })}
            </div>
            {renderError(key)}
          </fieldset>
        );
      }
      case 'checkbox':
        return (
          <div key={q.id} className={wrapClass}>
            <label className="zw-choice" htmlFor={fieldId}>
              <input
                id={fieldId}
                type="checkbox"
                checked={answers[q.id] === true}
                required={q.required}
                aria-required={q.required || undefined}
                aria-invalid={invalid(key)}
                aria-describedby={describedBy(key)}
                onChange={(e) => setAnswer(q.id, e.target.checked)}
              />
              <span>
                {q.label}
                {requiredMark(q.required)}
              </span>
            </label>
            {renderError(key)}
          </div>
        );
      default: {
        const type = q.type === 'phone' ? 'tel' : q.type === 'text' ? 'text' : q.type;
        return (
          <div key={q.id} className={wrapClass}>
            <label className="zw-label" htmlFor={fieldId}>
              {q.label}
              {requiredMark(q.required)}
            </label>
            <input
              id={fieldId}
              className="zw-input"
              type={type}
              inputMode={
                q.type === 'phone'
                  ? 'tel'
                  : q.type === 'email'
                    ? 'email'
                    : q.type === 'url'
                      ? 'url'
                      : undefined
              }
              value={typeof answers[q.id] === 'string' ? (answers[q.id] as string) : ''}
              placeholder={q.placeholder}
              maxLength={q.maxLength}
              required={q.required}
              aria-required={q.required || undefined}
              aria-invalid={invalid(key)}
              aria-describedby={describedBy(key)}
              onChange={(e) => setAnswer(q.id, e.target.value)}
            />
            {renderError(key)}
          </div>
        );
      }
    }
  };

  const busy = !!submitting;
  return (
    <form
      ref={formRef}
      className="zw-form"
      noValidate
      onSubmit={handleSubmit}
      aria-busy={busy || undefined}
    >
      <h3 ref={headingRef} className="zw-subtitle" tabIndex={-1}>
        {t('formTitle')}
      </h3>
      {children}

      <div className={`zw-field${errors.name ? ' zw-field--invalid' : ''}`}>
        <label className="zw-label" htmlFor={id('name')}>
          {t('formName')}
          {requiredMark(true)}
        </label>
        <input
          id={id('name')}
          className="zw-input"
          type="text"
          name="name"
          autoComplete="name"
          value={name}
          required
          aria-required="true"
          aria-invalid={invalid('name')}
          aria-describedby={describedBy('name')}
          onChange={(e) => {
            setName(e.target.value);
            clearError('name');
          }}
        />
        {renderError('name')}
      </div>

      <div className={`zw-field${errors.email ? ' zw-field--invalid' : ''}`}>
        <label className="zw-label" htmlFor={id('email')}>
          {t('formEmail')}
          {requiredMark(true)}
        </label>
        <input
          id={id('email')}
          className="zw-input"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          required
          aria-required="true"
          aria-invalid={invalid('email')}
          aria-describedby={describedBy('email')}
          onChange={(e) => {
            setEmail(e.target.value);
            clearError('email');
          }}
        />
        {renderError('email')}
      </div>

      <div className={`zw-field${errors.phone || errors.phoneCountry ? ' zw-field--invalid' : ''}`}>
        <label className="zw-label" htmlFor={id('phone')}>
          {t('formPhone')}
          {requiredMark(false)}
        </label>
        <div className="zw-phone">
          <Listbox
            id={id('phoneCountry')}
            className="zw-phone-country"
            aria-label={t('formPhoneCountry')}
            value={phoneCountry}
            placeholder={t('formPhoneCountryPlaceholder')}
            options={[
              ...countries.map((c) => ({ value: c.code, label: c.name })),
              ...(showAllCountries ? [] : [{ value: MORE, label: t('formMoreCountries') }]),
            ]}
            stayOpenFor={[MORE]}
            aria-invalid={invalid('phoneCountry')}
            aria-describedby={describedBy('phoneCountry')}
            onChange={(next) => {
              if (next === MORE) {
                setShowAllCountries(true);
                return;
              }
              setPhoneCountry(next);
              clearError('phoneCountry');
            }}
          />
          <input
            id={id('phone')}
            className="zw-input zw-phone-number"
            type="tel"
            name="phone"
            autoComplete="tel"
            inputMode="tel"
            value={phone}
            aria-invalid={invalid('phone')}
            aria-describedby={describedBy('phone')}
            onChange={(e) => {
              setPhone(e.target.value);
              clearError('phone');
            }}
          />
        </div>
        {renderError('phone')}
        {renderError('phoneCountry')}
      </div>

      {questions.map(renderQuestion)}

      {consentText && (
        <div className={`zw-field${errors.consent ? ' zw-field--invalid' : ''}`}>
          <label className="zw-choice" htmlFor={id('consent')}>
            <input
              id={id('consent')}
              type="checkbox"
              checked={consent}
              required
              aria-required="true"
              aria-invalid={invalid('consent')}
              aria-describedby={describedBy('consent')}
              onChange={(e) => {
                setConsent(e.target.checked);
                clearError('consent');
              }}
            />
            <span>{consentText}</span>
          </label>
          {renderError('consent')}
        </div>
      )}

      <div className="zw-actions">
        <button type="submit" className="zw-button" disabled={busy || disabled}>
          {busy ? t('formSubmitting') : (props.submitLabel ?? t('formSubmit'))}
        </button>
      </div>
    </form>
  );
}
